import { useCallback, useMemo, useState, useTransition } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useFiltersContext } from '@/context/FiltersContext/FiltersContextProvider';
import { FILTER_CONFIG } from '@/models/filter/filterConfig';
import { FilterType } from '@/models/filter/FilterType';
import { FilterState } from '@/types/filter/filterState';
import { useSessionStorageDraft } from './hooks/useSessionStorageDraft';
import { useDraftSync } from './hooks/useDraftSync';
import { useFilterInitialization } from './hooks/useFilterInitialization';
import { useVisibleFilterSections } from './hooks/useVisibleFilterSections';
import { SectionId } from './types/filterDraft.types';
import { FiltersForm } from './FiltersForm';
import { hasFilterValueChanges } from './utils/formUtils';
import styles from './FiltersOverlay.module.css';

const flattenFilterKeys = (filterKeys: (FilterType | FilterType[])[]): FilterType[] =>
  filterKeys.flatMap(filterKey => (Array.isArray(filterKey) ? filterKey : [filterKey]));

const EMPTY_FILTER_KEYS: readonly FilterType[] = [];

const FiltersOverlay = () => {
  const {
    filterState,
    handleApplyFilter,
    defaultFiltersState,
    filterOverlayVisible,
    setFilterOverlayVisible,
    filterConfig,
    commitOptionPaths,
    clearStagedOptionPaths,
  } = useFiltersContext();
  const configuredFilterKeys = filterConfig ?? EMPTY_FILTER_KEYS;

  const methods = useForm<FilterState>({
    defaultValues: filterState,
    values: filterState,
  });

  const { reset, setValue, getValues } = methods;

  const visibleSections = useVisibleFilterSections();
  const [activeSection, setActiveSection] = useState<SectionId>('recommendations');
  const [hasDraft, setHasDraft] = useState(false);
  const [isApplyingFilters, startTransition] = useTransition();

  const { getDraft, setSectionDraft, clearSectionDraft, clearAllDrafts, getSectionsWithData } =
    useSessionStorageDraft();
  const watchedValues = useWatch({ control: methods.control });

  const drawerFilterKeys = useMemo(
    () =>
      flattenFilterKeys(visibleSections.filter(section => !section.disabled).flatMap(section => section.filterKeys)),
    [visibleSections],
  );

  const configuredFilterKeySet = useMemo(() => new Set(configuredFilterKeys), [configuredFilterKeys]);

  const comparableFilterKeys = useMemo(
    () =>
      configuredFilterKeySet.size > 0
        ? drawerFilterKeys.filter(filterKey => configuredFilterKeySet.has(filterKey))
        : drawerFilterKeys,
    [drawerFilterKeys, configuredFilterKeySet],
  );

  const hasFilterChanges = useMemo(
    () => hasFilterValueChanges(comparableFilterKeys, watchedValues, filterState, defaultFiltersState),
    [comparableFilterKeys, watchedValues, filterState, defaultFiltersState],
  );

  const { isInitializedRef } = useFilterInitialization({
    isVisible: filterOverlayVisible,
    filterState,
    getDraft,
    getSectionsWithData,
    reset,
    setHasDraft,
  });

  const { cancelPendingSync } = useDraftSync({
    methods,
    isVisible: filterOverlayVisible,
    isInitializedRef,
    baselineValues: filterState,
    defaultValues: defaultFiltersState,
    setSectionDraft,
    clearSectionDraft,
  });

  const getDefaultValue = useCallback(
    (filterKey: FilterType) =>
      Object.hasOwn(defaultFiltersState, filterKey)
        ? defaultFiltersState[filterKey]
        : FILTER_CONFIG[filterKey].defaultValue,
    [defaultFiltersState],
  );

  const handleClearSection = useCallback(
    (filtersToClear: (FilterType | FilterType[])[]) => {
      filtersToClear.flat().forEach(filterKey => {
        setValue(filterKey, getDefaultValue(filterKey), { shouldValidate: true, shouldDirty: true });
      });
    },
    [setValue, getDefaultValue],
  );

  const handleCancel = useCallback(() => {
    reset(filterState, { keepDirtyValues: false });
    cancelPendingSync();
    clearAllDrafts();
    clearStagedOptionPaths?.();
    setHasDraft(false);
    setFilterOverlayVisible(false);
  }, [reset, filterState, cancelPendingSync, clearAllDrafts, clearStagedOptionPaths, setFilterOverlayVisible]);

  // The drawer keeps the draft on close, but the registry must keep describing
  // the applied filterState, so the staged paths are discarded either way.
  const handleCloseDrawer = useCallback(() => {
    clearStagedOptionPaths?.();
    setFilterOverlayVisible(false);
  }, [clearStagedOptionPaths, setFilterOverlayVisible]);

  const handleClearAll = useCallback(() => {
    const nextValues = { ...getValues() };

    comparableFilterKeys.forEach(filterKey => {
      nextValues[filterKey] = getDefaultValue(filterKey);
    });

    reset(nextValues, { keepDefaultValues: true });
  }, [reset, getValues, comparableFilterKeys, getDefaultValue]);

  const onSubmit = useCallback(
    (formValues: FilterState) => {
      cancelPendingSync();
      clearAllDrafts();
      setHasDraft(false);
      setFilterOverlayVisible(false);
      startTransition(() => {
        commitOptionPaths?.();
        handleApplyFilter({ ...filterState, ...formValues });
      });
    },
    [handleApplyFilter, filterState, cancelPendingSync, clearAllDrafts, commitOptionPaths, setFilterOverlayVisible],
  );

  const canSubmit = hasFilterChanges;
  const shouldShowDraftPulse = hasDraft && hasFilterChanges;

  return (
    <FormProvider {...methods}>
      {isApplyingFilters && (
        <div
          aria-busy="true"
          aria-hidden="true"
          className={styles.filterApplyPendingOverlay}
          data-testid="filters-apply-pending-overlay"
        />
      )}
      <FiltersForm
        isOpen={filterOverlayVisible}
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        handleClearSection={handleClearSection}
        handleCloseDrawer={handleCloseDrawer}
        handleCancel={handleCancel}
        handleClearAll={handleClearAll}
        onSubmit={onSubmit}
        canSubmit={canSubmit}
        hasDraft={shouldShowDraftPulse}
      />
    </FormProvider>
  );
};

export default FiltersOverlay;
